import { isDate, isEmpty, isNumeric } from '@nirvana/shared/basic-types/object';
import { NavigationQueryObject } from './navigation.interfaces';

/**
 * Build browser hash path
 *
 * @param {string} path
 * @param {?object} query
 */
export function buildHash(path: string, query?: object | null | undefined): string {
  const hash = '#!' + path;

  if (!query || Object.keys(query).length === 0) {
    return hash;
  }

  return hash + '?' + toQueryString(query, true);
}

/**
 * Converts a `name` - `value` pair to an array of objects with support for nested structures. Useful to construct
 * query strings. For example:
 *
 *     var objects = Ext.Object.toQueryObjects('hobbies', ['reading', 'cooking', 'swimming']);
 *
 *     // objects then equals:
 *     [
 *         { name: 'hobbies', value: 'reading' },
 *         { name: 'hobbies', value: 'cooking' },
 *         { name: 'hobbies', value: 'swimming' },
 *     ];
 *
 *     var objects = Ext.Object.toQueryObjects('dateOfBirth', {
 *         day: 3,
 *         month: 8,
 *         year: 1987,
 *         extra: {
 *             hour: 4
 *             minute: 30
 *         }
 *     }, true); // Recursive
 *
 *     // objects then equals:
 *     [
 *         { name: 'dateOfBirth[day]', value: 3 },
 *         { name: 'dateOfBirth[month]', value: 8 },
 *         { name: 'dateOfBirth[year]', value: 1987 },
 *         { name: 'dateOfBirth[extra][hour]', value: 4 },
 *         { name: 'dateOfBirth[extra][minute]', value: 30 },
 *     ];
 *
 * @param {String} name
 * @param {Object/Array} value
 * @param {Boolean} [recursive=false] True to traverse object recursively
 * @return {Array}
 */
export function toQueryObjects(name: string, value: any, recursive: boolean = false): NavigationQueryObject[] {
  const self = toQueryObjects;
  let objects: NavigationQueryObject[] = [],
    i,
    ln;

  if (Array.isArray(value)) {
    for (i = 0, ln = value.length; i < ln; i++) {
      if (recursive) {
        objects = objects.concat(self(name + '[' + i + ']', value[i], true));
      } else {
        objects.push({
          name: name,
          value: value[i],
        });
      }
    }
  } else if (value !== null && toString.call(value) === '[object Object]') {
    for (i in value) {
      if (value.hasOwnProperty(i)) {
        if (recursive) {
          objects = objects.concat(self(name + '[' + i + ']', value[i], true));
        } else {
          objects.push({
            name: name,
            value: value[i],
          });
        }
      }
    }
  } else {
    objects.push({
      name: name,
      value: value,
    });
  }

  return objects;
}

/**
 * Takes an object and converts it to an encoded query string.
 *
 * Non-recursive:
 *
 *     Ext.Object.toQueryString({foo: 1, bar: 2}); // returns "foo=1&bar=2"
 *     Ext.Object.toQueryString({foo: null, bar: 2}); // returns "foo=&bar=2"
 *     Ext.Object.toQueryString({'some price': '$300'}); // returns "some%20price=%24300"
 *     Ext.Object.toQueryString({date: new Date(2011, 0, 1)}); // returns "date=%222011-01-01T00%3A00%3A00%22"
 *     Ext.Object.toQueryString({colors: ['red', 'green', 'blue']}); // returns "colors=red&colors=green&colors=blue"
 *
 * Recursive:
 *
 *     Ext.Object.toQueryString({
 *         username: 'Jacky',
 *         dateOfBirth: {
 *             day: 1,
 *             month: 2,
 *             year: 1911
 *         },
 *         hobbies: ['coding', 'eating', 'sleeping', ['nested', 'stuff']]
 *     }, true); // returns the following string (broken down and url-decoded for ease of reading purpose):
 *     // username=Jacky
 *     //    &dateOfBirth[day]=1&dateOfBirth[month]=2&dateOfBirth[year]=1911
 *     //    &hobbies[0]=coding&hobbies[1]=eating&hobbies[2]=sleeping&hobbies[3][0]=nested&hobbies[3][1]=stuff
 *
 * @param {Object} object The object to encode
 * @param {Boolean} [recursive=false] Whether or not to interpret the object in recursive format.
 * (PHP / Ruby on Rails servers and similar).
 * @return {String} queryString
 */
export function toQueryString(object: any, recursive: boolean = false): string {
  const params: any[] = [];
  let paramObjects: NavigationQueryObject[] = [],
    i,
    j,
    ln,
    paramObject,
    value;

  for (i in object) {
    if (object.hasOwnProperty(i)) {
      paramObjects = paramObjects.concat(toQueryObjects(i, object[i], recursive));
    }
  }

  for (j = 0, ln = paramObjects.length; j < ln; j++) {
    paramObject = paramObjects[j];
    value = paramObject.value;

    if (isEmpty(value)) {
      value = '';
    } else if (isDate(value)) {
      value = value.toString();
    }

    params.push(encodeURIComponent(paramObject.name) + '=' + encodeURIComponent(String(value)));
  }

  return params.join('&');
}

/**
 * Converts a query string back into an object.
 *
 * Non-recursive:
 *
 *     Ext.Object.fromQueryString("foo=1&bar=2"); // returns {foo: '1', bar: '2'}
 *     Ext.Object.fromQueryString("foo=&bar=2"); // returns {foo: null, bar: '2'}
 *     Ext.Object.fromQueryString("some%20price=%24300"); // returns {'some price': '$300'}
 *     Ext.Object.fromQueryString("colors=red&colors=green&colors=blue"); // returns {colors: ['red', 'green', 'blue']}
 *
 * Recursive:
 *
 *     Ext.Object.fromQueryString(
 *         "username=Jacky&"+
 *         "dateOfBirth[day]=1&dateOfBirth[month]=2&dateOfBirth[year]=1911&"+
 *         "hobbies[0]=coding&hobbies[1]=eating&hobbies[2]=sleeping&"+
 *         "hobbies[3][0]=nested&hobbies[3][1]=stuff", true);
 *
 *     // returns
 *     {
 *         username: 'Jacky',
 *         dateOfBirth: {
 *             day: '1',
 *             month: '2',
 *             year: '1911'
 *         },
 *         hobbies: ['coding', 'eating', 'sleeping', ['nested', 'stuff']]
 *     }
 *
 * @param {String} queryString The query string to decode
 * @param {Boolean} [recursive=false] Whether or not to recursively decode the string. This format is supported by
 * PHP / Ruby on Rails servers and similar.
 * @return {Object}
 */
export function fromQueryString<T extends object>(queryString: string, recursive: boolean = false): T {
  const parts = queryString.replace(/^\?/, '').split('&'),
    object: object = {};
  let temp, components, name, value, i, ln, part, j, subLn, matchedKeys, matchedName, keys, key, nextKey;

  for (i = 0, ln = parts.length; i < ln; i++) {
    part = parts[i];

    if (part.length > 0) {
      components = part.split('=');
      name = decodeURIComponent(components[0]);
      value = components[1] !== undefined ? decodeURIComponent(components[1]) : '';

      if (!recursive) {
        if (object.hasOwnProperty(name)) {
          if (!Array.isArray(object[name])) {
            object[name] = [object[name]];
          }

          object[name].push(value);
        } else {
          object[name] = value;
        }
      } else {
        matchedKeys = name.match(/(\[):?([^\]]*)\]/g);
        matchedName = name.match(/^([^\[]+)/);

        name = matchedName[0];
        keys = [];

        if (matchedKeys === null) {
          object[name] = value;
          continue;
        }

        for (j = 0, subLn = matchedKeys.length; j < subLn; j++) {
          key = matchedKeys[j];
          key = key.length === 2 ? '' : key.substring(1, key.length - 1);
          keys.push(key);
        }

        keys.unshift(name);

        temp = object;

        for (j = 0, subLn = keys.length; j < subLn; j++) {
          key = keys[j];

          if (j === subLn - 1) {
            if (Array.isArray(temp) && key === '') {
              temp.push(value);
            } else {
              temp[key] = value;
            }
          } else {
            if (temp[key] === undefined || typeof temp[key] === 'string') {
              nextKey = keys[j + 1];

              temp[key] = isNumeric(nextKey) || nextKey === '' ? [] : {};
            }

            temp = temp[key];
          }
        }
      }
    }
  }

  return object as any as T;
}

export function buildUrl(path: string, query?: object | null | undefined): string {
  const hash = buildHash(path, query);
  return `${window.location.protocol}//${window.location.host}/` + hash;
}
